import { handleGoBack } from './handleGoBack.js';
import { handleGoForward } from './handleGoForward.js';
import { type InitialState, type State } from './state.js';
import { getEnabledPayouts } from './utils.js';

export type Action =
  | Cancel
  | ConnectPaypal
  | GoBack
  | GoForward
  | RefreshAfterRedirectFromStripe
  | SetInitialState
  | SetPayoutAccountEligibility
  | SetPayoutMethod
  | SetStripeBusinessType
  | Start
  | SyncPayoneer;

type Cancel = {
  type: 'CANCEL';
};

type ConnectPaypal = {
  payload: {
    paypalAuthenticationCode: string;
  };
  type: 'CONNECT_PAYPAL';
};

type GoBack = {
  type: 'GO_BACK';
};

type GoForward = {
  type: 'GO_FORWARD';
};

type RefreshAfterRedirectFromStripe = {
  payload: {
    paymentMethod:
      | 'PAYOUT_Stripe_Local_Bank'
      | 'PAYOUT_Stripe_Local_Debit_Card';
  };
  type: 'REFRESH_AFTER_STRIPE_ACCOUNT';
};

type SetInitialState = {
  payload: InitialState;
  type: 'SET_INITIAL_STATE';
};

type SetPayoutAccountEligibility = {
  payload: State['payoutAccountEligibility'];
  type: 'SET_PAYOUT_ACCOUNT_ELIGIBILITY';
};

type SetPayoutMethod = {
  payload: State['selectedPayoutType'];
  type: 'SET_PAYOUT_METHOD';
};

type SetStripeBusinessType = {
  payload: {
    businessType: 'COMPANY' | 'INDIVIDUAL';
  };
  type: 'SET_STRIPE_BUSINESS_TYPE';
};

type Start = {
  payload: {};
  type: 'START';
};

type SyncPayoneer = {
  type: 'SYNC_PAYONEER';
};

// This is being disabled because the exhaustive check rule renders this check unnecessary
// If this rule is not disabled, the last return statement will be 'unreachable code'
// eslint-disable-next-line consistent-return
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'CANCEL': {
      return {
        ...state,
        view: null,
      };
    }

    case 'CONNECT_PAYPAL': {
      return {
        ...state,
        payoutTypesEnabled: getEnabledPayouts(state),
        paypalAuthenticationCode: action.payload.paypalAuthenticationCode,
        view: 'VIEW_Paypal_Return' as State['view'],
      };
    }

    case 'GO_BACK': {
      return handleGoBack(state);
    }

    case 'GO_FORWARD': {
      return handleGoForward(state);
    }

    case 'REFRESH_AFTER_STRIPE_ACCOUNT': {
      return {
        ...state,
        payoutTypesEnabled: getEnabledPayouts(state),
        selectedPayoutType: action.payload.paymentMethod,
        view: 'VIEW_Stripe_Return',
      };
    }

    case 'SET_INITIAL_STATE': {
      return {
        ...state,
        ...action.payload,
      };
    }

    case 'SET_PAYOUT_ACCOUNT_ELIGIBILITY': {
      return {
        ...state,
        payoutAccountEligibility: action.payload,
      };
    }

    case 'SET_PAYOUT_METHOD': {
      return {
        ...state,
        selectedPayoutType: action.payload,
      };
    }

    case 'SET_STRIPE_BUSINESS_TYPE': {
      return {
        ...state,
        selectedBusinessType: action.payload.businessType,
      };
    }

    case 'START': {
      const view: State['view'] = state.canEditCountry
        ? 'VIEW_Select_Country'
        : 'VIEW_Select_Payout_Method';

      return {
        ...state,
        payoutTypesEnabled: getEnabledPayouts(state),
        view,
      };
    }

    case 'SYNC_PAYONEER': {
      return {
        ...state,
        payoutTypesEnabled: getEnabledPayouts(state),
        view: 'VIEW_Payoneer_Return' as State['view'],
      };
    }
  }
};
